import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { requestAdminOtp, adminSignin, type AdminSigninPayload } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { useCooldown } from '@shared/hooks/use-cooldown';
import { validateEmail } from '@shared/utils/validation';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { EyeIcon, EyeOffIcon } from '@/components/layout/icons';
import { AuthCard } from './auth-card';

export function LoginPage() {
  const [mode, setMode] = useState<'password' | 'otp'>('password');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);

  const { secondsLeft, active: onCooldown, start: startCooldown } = useCooldown(30);

  // OTP login mutation (legacy / Super Admin)
  const otpMutation = useMutation({
    mutationFn: (e: string) => requestAdminOtp(e),
    onSuccess: () => navigate(`/otp?email=${encodeURIComponent(email.trim())}`),
    onSettled: () => startCooldown(),
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  // Password signin mutation (registered admins)
  const signinMutation = useMutation({
    mutationFn: (payload: AdminSigninPayload) => adminSignin(payload),
    onSuccess: (data) => {
      getTokenStore().setAccessToken(data.accessToken, 'admin');
      qc.clear();
      setSession(data.user);
      navigate('/', { replace: true });
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err));
    },
  });

  function onPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailTouched(true);
    const result = validateEmail(email);
    if (!result.isValid) {
      setEmailError(result.error);
      return;
    }
    setEmailError(null);
    const em = email.trim().toLowerCase();
    if (!password) return;
    signinMutation.mutate({ email: em, password });
  }

  function onOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailTouched(true);
    const result = validateEmail(email);
    if (!result.isValid) {
      setEmailError(result.error);
      return;
    }
    setEmailError(null);
    if (onCooldown || otpMutation.isPending) return;
    const v = email.trim().toLowerCase();
    otpMutation.mutate(v);
  }

  function handleEmailChange(val: string) {
    setEmail(val);
    if (emailTouched) {
      const result = validateEmail(val);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  function handleEmailBlur() {
    setEmailTouched(true);
    if (email.trim()) {
      const result = validateEmail(email);
      setEmailError(result.isValid ? null : result.error);
    }
  }

  return (
    <AuthCard>
      <div className="space-y-5">
        <div className="text-center">
          <h1 className="text-lg font-bold text-gray-900">Sign in to Admin</h1>
        </div>

        {/* Tab Switcher */}
        <div className="flex rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => {
              setMode('password');
              setEmailError(null);
            }}
            className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-all ${mode === 'password'
                ? 'bg-white text-brand-900 shadow-xs'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Password Sign In
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('otp');
              setEmailError(null);
            }}
            className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-all ${mode === 'otp'
                ? 'bg-white text-brand-900 shadow-xs'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            OTP Sign In
          </button>
        </div>

        {mode === 'password' ? (
          <form className="space-y-3.5" onSubmit={onPasswordSubmit} noValidate>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Admin Email
              </label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="admin@ecregi.com"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                className={emailError ? 'border-red-500 focus:border-red-600 focus:ring-red-500/30' : undefined}
                required
                autoFocus
              />
              {emailError && (
                <p className="mt-1 text-xs font-medium text-red-600">{emailError}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-hidden p-1 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {signinMutation.isError && (
              <p className="text-xs text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">
                {apiErrorMessage(signinMutation.error)}
              </p>
            )}

            <Button
              type="submit"
              block
              disabled={signinMutation.isPending}
              className="mt-2"
            >
              {signinMutation.isPending ? 'Signing in…' : 'Sign in with Password'}
            </Button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={onOtpSubmit} noValidate>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Email Address
              </label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="superadmin@ecregi.com"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                className={emailError ? 'border-red-500 focus:border-red-600 focus:ring-red-500/30' : undefined}
                required
                autoFocus
              />
              {emailError && (
                <p className="mt-1 text-xs font-medium text-red-600">{emailError}</p>
              )}
            </div>

            {otpMutation.isError && (
              <p className="text-xs text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">
                {apiErrorMessage(otpMutation.error)}
              </p>
            )}

            <Button
              type="submit"
              block
              disabled={otpMutation.isPending || onCooldown}
              className="mt-2"
            >
              {otpMutation.isPending
                ? 'Sending…'
                : onCooldown
                  ? `Try again in ${secondsLeft}s`
                  : 'Send One-Time Code'}
            </Button>
          </form>
        )}

        <div className="border-t border-gray-100 pt-3 text-center">
          <Link
            to="/register"
            className="text-xs font-semibold text-brand-700 hover:text-brand-800"
          >
            Need an admin account? Register as Admin →
          </Link>
        </div>
      </div>
    </AuthCard>
  );
}
